import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, NavLink, Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import WahooCallbackPage from './WahooCallbackPage';
import HammerheadCallbackPage from './HammerheadCallbackPage';
import MobileSyncPage from './MobileSyncPage';
import MobileHandoffPage from './MobileHandoffPage';
import MobileProfilePage from './MobileProfilePage';
import MobileAboutPage from './MobileAboutPage';
import MobileRaceVisualizer from './MobileRaceVisualizer';
import MobileRacesPage from './MobileRacesPage';
import GroupRideDetailsPage from './GroupRideDetailsPage';
import { TermsOfService, EULA, PrivacyPolicy } from './LegalDocs';
import WelcomeProfileModal from '../components/WelcomeProfileModal';
import { userService } from '../services/userService';

// Icons for Navbar
const HomeIcon = (props: any) => <svg {...props} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h7.5" /></svg>;
const UserCircleIcon = (props: any) => <svg {...props} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" /></svg>;
const InformationCircleIcon = (props: any) => <svg {...props} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" /></svg>;
const TrophyIcon = (props: any) => <svg {...props} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0" /></svg>;


const MobileAuthFlow: React.FC = () => {
    const { currentUser, signInWithGoogle, login, signup, logout, resetPassword } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const returnTo = searchParams.get('returnTo');
    const mode = searchParams.get('mode');
    const navigate = useNavigate();

    const [isLoginMode, setIsLoginMode] = useState(mode !== 'signup');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Sync isLoginMode whenever query param `mode` changes (e.g. clicking header buttons)
    useEffect(() => {
        setIsLoginMode(mode !== 'signup');
        setError(null);
        setSuccessMessage(null);
    }, [mode]);

    const toggleMode = () => {
        const nextIsLogin = !isLoginMode;
        setIsLoginMode(nextIsLogin);
        setError(null);
        setSuccessMessage(null);
        setPassword('');
        setConfirmPassword('');

        const nextParams = new URLSearchParams(searchParams);
        if (!nextIsLogin) {
            nextParams.set('mode', 'signup');
        } else {
            nextParams.delete('mode');
        }
        setSearchParams(nextParams);
    };

    // Dev/UAT bypass logic (matches App.tsx)
    const isDevOrUat = (import.meta as any).env.MODE === 'development' || (import.meta as any).env.MODE === 'uat';
    const emailLower = currentUser?.email?.toLowerCase() || '';
    const isTestUser = emailLower.includes('test') || emailLower.endsWith('@example.com');
    const isVerified = currentUser?.emailVerified || (isDevOrUat && isTestUser);

    const handleAuth = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccessMessage(null);

        if (!isLoginMode && password !== confirmPassword) {
            return setError("Passwords do not match");
        }

        if (password.length < 6) {
            return setError("Password must be at least 6 characters");
        }

        setLoading(true);
        
        try {
            if (isLoginMode) {
                await login(email, password);
                if (returnTo) navigate(returnTo, { replace: true });
            } else {
                await signup(email, password);
            }
        } catch (err: any) {
            console.error(err);
            let msg = "Failed to authenticate";
            if (err.code === 'auth/user-not-found') msg = "No account found with this email";
            if (err.code === 'auth/wrong-password') msg = "Incorrect password";
            if (err.code === 'auth/email-already-in-use') msg = "Email already in use";
            if (err.code === 'auth/weak-password') msg = "Password is too weak";
            if (err.code === 'auth/invalid-credential') msg = "Invalid email or password";
            setError(msg || err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setError(null);
        setLoading(true);
        try {
            await signInWithGoogle();
            if (returnTo) navigate(returnTo, { replace: true });
        } catch (err: any) {
            setError("Google sign-in failed.");
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!email) {
            return setError("Please enter your email address to reset your password.");
        }
        try {
            setLoading(true);
            setError(null);
            if (resetPassword) {
                await resetPassword(email);
                alert("Password reset email sent! Check your inbox.");
            } else {
                setError("Password reset is not configured.");
            }
        } catch (err: any) {
            console.error(err);
            setError("Failed to reset password. Verify your email address.");
        } finally {
            setLoading(false);
        }
    };

    if (!currentUser) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen px-6 py-12 animate-fade-in">
                <div className="text-center mb-8 w-full">
                    <h1 className="font-sans text-4xl font-bold text-white tracking-wide mb-2">
                        RaceLine<span className="text-cyan-400">IQ</span>
                    </h1>
                    <p className="text-gray-400 text-base mb-4">Don't just train harder.<br/>Race smarter.</p>
                    <div className="inline-block bg-gray-800/80 px-4 py-1.5 rounded-full border border-gray-700 text-sm font-semibold text-cyan-400 shadow-sm">
                        {isLoginMode ? "Sign In" : "Create Account"}
                    </div>
                </div>

                <div className="w-full max-w-sm space-y-6">
                    {successMessage && <div className="p-3 bg-green-900/50 text-green-200 border border-green-500 rounded-lg text-sm text-center">{successMessage}</div>}

                    <div className="space-y-4">
                        <button
                            onClick={handleGoogleSignIn}
                            disabled={loading}
                            className="w-full flex items-center justify-center px-4 py-4 bg-white text-gray-900 font-bold rounded-xl hover:bg-gray-100 transition-colors shadow-lg active:scale-95"
                        >
                            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.26.81-.58z" />
                                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                            </svg>
                            Continue with Google
                        </button>

                        <div className="flex items-center text-gray-500 text-sm">
                            <div className="flex-1 border-t border-gray-700"></div>
                            <span className="px-3">or</span>
                            <div className="flex-1 border-t border-gray-700"></div>
                        </div>
                    </div>

                    <form onSubmit={handleAuth} className="space-y-4">
                        {error && <div className="p-3 bg-red-900/50 text-red-200 border border-red-500 rounded-lg text-sm text-center">{error}</div>}
                        <input
                            type="email"
                            required
                            placeholder="Email address"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-4 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-lg"
                        />
                        <div>
                            <input
                                type="password"
                                required
                                placeholder="Password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-4 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-lg mb-2"
                            />
                            {isLoginMode && (
                                <div className="text-right">
                                    <button 
                                        type="button" 
                                        onClick={handleForgotPassword}
                                        className="text-sm text-cyan-400 hover:text-cyan-300 font-medium"
                                    >
                                        Forgot Password?
                                    </button>
                                </div>
                            )}
                        </div>

                        {!isLoginMode && (
                            <div>
                                <input
                                    type="password"
                                    required
                                    placeholder="Confirm Password"
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-4 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-lg mb-2"
                                />
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-4 rounded-xl transition-colors active:scale-95"
                        >
                            {loading ? 'Processing...' : (isLoginMode ? 'Sign In' : 'Create Account')}
                        </button>
                    </form>

                    <div className="text-center">
                        <button type="button" onClick={toggleMode} className="text-cyan-400 font-medium py-2 px-4 rounded-lg active:bg-gray-800">
                            {isLoginMode ? "Need an account? Sign Up" : "Already have an account? Sign In"}
                        </button>
                    </div>

                    <div className="text-center mt-6">
                        <p className="text-xs text-gray-500">
                            By continuing, you agree to our{' '}
                            <Link to="/terms" className="text-cyan-400 hover:underline">Terms of Service</Link>
                            ,{' '}
                            <Link to="/eula" className="text-cyan-400 hover:underline">EULA</Link>
                            , and{' '}
                            <Link to="/privacy" className="text-cyan-400 hover:underline">Privacy Policy</Link>.
                        </p>
                    </div>
                </div>

                {/* Features Section */}
                <div className="mt-16 w-full max-w-sm border-t border-gray-800 pt-10">
                    <h2 className="text-2xl font-bold text-white mb-6 text-center">Features overview</h2>
                    <div className="space-y-4 text-left">
                        <div className="bg-gray-800/50 p-5 rounded-xl border border-gray-700">
                            <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center mb-4 border border-cyan-500/30">
                                <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            </div>
                            <h3 className="text-lg font-bold text-white mb-2">Playback Engine</h3>
                            <p className="text-sm text-gray-300">Review races with not just you but everyone else.</p>
                            <p className="text-xs text-gray-500 mt-2">*Only racers who add their event files to RaceLineIQ will show up.</p>
                        </div>
                        <div className="bg-gray-800/50 p-5 rounded-xl border border-gray-700">
                            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4 border border-blue-500/30">
                                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                            </div>
                            <h3 className="text-lg font-bold text-white mb-2">Automatic Cataloging</h3>
                            <p className="text-sm text-gray-300">Your race file is placed in the correct race for you automatically.</p>
                            <p className="text-xs text-gray-500 mt-2">*Requires account integration.</p>
                        </div>
                        <div className="bg-gray-800/50 p-5 rounded-xl border border-gray-700">
                            <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center mb-4 border border-indigo-500/30">
                                <svg className="w-5 h-5 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                            </div>
                            <h3 className="text-lg font-bold text-white mb-2">Drafting Analytics</h3>
                            <p className="text-sm text-gray-300">See how much time you spent in the draft and how much energy you saved.</p>
                        </div>
                        <div className="bg-gray-800/50 p-5 rounded-xl border border-gray-700">
                            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4 border border-purple-500/30">
                                <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                            </div>
                            <h3 className="text-lg font-bold text-white mb-2">Performance Insights</h3>
                            <p className="text-sm text-gray-300">Get a full report on where you excelled or can improve your performance on race day.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!isVerified) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center animate-fade-in">
                <div className="w-20 h-20 bg-cyan-500/20 rounded-full flex items-center justify-center border border-cyan-500/50 mb-6">
                    <svg className="w-10 h-10 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v10a2 2 0 002 2z" />
                    </svg>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Check your email</h2>
                <p className="text-gray-400 mb-8 max-w-xs">
                    We've sent a verification link to <span className="text-cyan-400">{currentUser.email}</span>. Please verify your email to continue.
                </p>
                <button onClick={() => window.location.reload()} className="w-full max-w-sm bg-cyan-600 text-white font-bold py-4 rounded-xl active:scale-95 mb-4">
                    I have verified my email
                </button>
                <button onClick={() => logout()} className="text-gray-500 py-2">Sign Out</button>
            </div>
        );
    }

    // This component now only handles auth and verification screens.
    // It will be rendered when the user is not authenticated or not verified.
    return null; // The logic is handled inside the main MobileApp component now.
}

const MobileBottomNavbar: React.FC = () => {
    const activeClass = "text-cyan-400";
    const inactiveClass = "text-gray-400 hover:text-white";

    return (
        <div className="fixed bottom-0 left-0 right-0 h-20 bg-gray-800/90 backdrop-blur-lg border-t border-gray-700 z-50">
            <div className="flex justify-around items-center h-full max-w-md mx-auto">
                <NavLink to="/" end className={({ isActive }) => isActive ? activeClass : inactiveClass}>
                    <div className="flex flex-col items-center justify-center">
                        <HomeIcon className="w-7 h-7" />
                        <span className="text-xs mt-1">Sync</span>
                    </div>
                </NavLink>
                <NavLink 
                    to="/races" 
                    onClick={() => {
                        sessionStorage.removeItem('mobile_races_scroll_y');
                        window.scrollTo(0, 0);
                    }}
                    className={({ isActive }) => isActive ? activeClass : inactiveClass}
                >
                    <div className="flex flex-col items-center justify-center">
                        <TrophyIcon className="w-7 h-7" />
                        <span className="text-xs mt-1">Events</span>
                    </div>
                </NavLink>
                <NavLink to="/profile" className={({ isActive }) => isActive ? activeClass : inactiveClass}>
                    <div className="flex flex-col items-center justify-center">
                        <UserCircleIcon className="w-7 h-7" />
                        <span className="text-xs mt-1">Profile</span>
                    </div>
                </NavLink>
                <NavLink to="/about" className={({ isActive }) => isActive ? activeClass : inactiveClass}>
                    <div className="flex flex-col items-center justify-center">
                        <InformationCircleIcon className="w-7 h-7" />
                        <span className="text-xs mt-1">About</span>
                    </div>
                </NavLink>
            </div>
        </div>
    );
};


const MobileApp: React.FC = () => {
    const { currentUser, userProfile, loading, refreshUserProfile } = useAuth();
    const location = useLocation();
    const [showWelcome, setShowWelcome] = useState(false);

    // Scroll to top when opening detail routes or switching primary tabs
    useEffect(() => {
        if (
            location.pathname.startsWith('/race/') || 
            location.pathname.startsWith('/ride/') || 
            location.pathname === '/' || 
            location.pathname === '/profile' || 
            location.pathname === '/about'
        ) {
            window.scrollTo(0, 0);
        }
    }, [location.pathname]);

    const isDevOrUat = (import.meta as any).env.MODE === 'development' || (import.meta as any).env.MODE === 'uat';
    const emailLower = currentUser?.email?.toLowerCase() || '';
    const isTestUser = emailLower.includes('test') || emailLower.endsWith('@example.com');
    const isVerified = currentUser?.emailVerified || (isDevOrUat && isTestUser);

    useEffect(() => {
        if (!loading && currentUser && isVerified && (!userProfile || !userProfile.firstName)) {
            const metadata = currentUser.metadata;
            if (metadata && metadata.creationTime) {
                const creationTime = new Date(metadata.creationTime).getTime();
                const now = Date.now();
                if (now - creationTime > 7 * 24 * 60 * 60 * 1000) {
                    return;
                }
            }
            const hasDismissed = localStorage.getItem(`raceLineIQ_welcome_dismissed_${currentUser.uid}`);
            if (!hasDismissed) {
                setShowWelcome(true);
            }
        }
    }, [loading, currentUser, userProfile, isVerified]);

    const handleCloseWelcome = async (didSave: boolean) => {
        if (currentUser) {
            localStorage.setItem(`raceLineIQ_welcome_dismissed_${currentUser.uid}`, 'true');
        }
        setShowWelcome(false);
        if (didSave) {
            await refreshUserProfile();
        } else {
            if (currentUser && !userProfile) {
                try {
                    await userService.updateUserProfile(currentUser.uid, {
                        email: currentUser.email || '',
                    });
                    await refreshUserProfile();
                } catch (e) {
                    console.error("Failed to dismiss welcome modal permanently", e);
                }
            }
        }
    };

    return (
        <div className="bg-gray-900 min-h-screen text-gray-100 font-sans">
            {!currentUser && (() => {
                const searchParams = new URLSearchParams(location.search);
                const existingReturnTo = searchParams.get('returnTo');
                const safeReturnTo = location.pathname === '/auth'
                    ? (existingReturnTo || '/')
                    : (location.pathname + location.search);
                const isSignupMode = searchParams.get('mode') === 'signup';

                return (
                    <div className="bg-gray-800/95 backdrop-blur-md border-b border-gray-700 px-4 py-3 flex items-center justify-between sticky top-0 z-40">
                        <Link to="/" className="text-white text-lg font-bold">
                            RaceLine<span className="text-cyan-400">IQ</span>
                        </Link>
                        <div className="flex items-center gap-2">
                            <Link
                                to={`/auth?returnTo=${encodeURIComponent(safeReturnTo)}`}
                                className={`text-xs font-semibold px-2 py-1 transition-colors ${
                                    location.pathname === '/auth' && !isSignupMode
                                        ? 'text-cyan-400 font-bold'
                                        : 'text-gray-300 hover:text-white'
                                }`}
                            >
                                Sign In
                            </Link>
                            <Link
                                to={`/auth?mode=signup&returnTo=${encodeURIComponent(safeReturnTo)}`}
                                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors shadow-sm ${
                                    location.pathname === '/auth' && isSignupMode
                                        ? 'bg-cyan-300 text-gray-950 ring-2 ring-cyan-400'
                                        : 'bg-cyan-400 text-gray-950 hover:bg-cyan-300'
                                }`}
                            >
                                Get Started
                            </Link>
                        </div>
                    </div>
                );
            })()}
            <div className={currentUser ? "pb-20" : ""}>
                <Routes>
                    {/* Legal Routes */}
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/eula" element={<EULA />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />

                    {/* Auth Route */}
                    <Route
                        path="/auth"
                        element={currentUser && isVerified ? <Navigate to="/" replace /> : <MobileAuthFlow />}
                    />

                    {/* Public Content Routes */}
                    <Route path="/about" element={<MobileAboutPage />} />
                    <Route path="/race/:raceId/category/:categoryId" element={<MobileRaceVisualizer />} />
                    <Route path="/race/:raceId" element={<MobileRaceVisualizer />} />
                    <Route path="/ride/:rideId" element={<GroupRideDetailsPage />} />

                    {/* Protected Routes */}
                    <Route
                        path="/"
                        element={
                            currentUser && isVerified ? (
                                <MobileSyncPage />
                            ) : (
                                <MobileAuthFlow />
                            )
                        }
                    />
                    <Route
                        path="/races"
                        element={
                            currentUser && isVerified ? (
                                <MobileRacesPage />
                            ) : (
                                <Navigate to="/auth?returnTo=/races" replace />
                            )
                        }
                    />
                    <Route
                        path="/profile"
                        element={
                            currentUser && isVerified ? (
                                <MobileProfilePage />
                            ) : (
                                <Navigate to="/auth?returnTo=/profile" replace />
                            )
                        }
                    />
                    <Route
                        path="/handoff"
                        element={
                            currentUser && isVerified ? (
                                <MobileHandoffPage />
                            ) : (
                                <Navigate to="/auth?returnTo=/handoff" replace />
                            )
                        }
                    />
                    <Route
                        path="/wahoo-callback"
                        element={
                            currentUser && isVerified ? (
                                <WahooCallbackPage />
                            ) : (
                                <Navigate to="/auth" replace />
                            )
                        }
                    />
                    <Route
                        path="/hammerhead-callback"
                        element={
                            currentUser && isVerified ? (
                                <HammerheadCallbackPage />
                            ) : (
                                <Navigate to="/auth" replace />
                            )
                        }
                    />

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </div>
            {currentUser && <MobileBottomNavbar />}
            <WelcomeProfileModal isOpen={showWelcome && !!isVerified} onClose={handleCloseWelcome} />
        </div>
    );
};

export default MobileApp;